import React, { useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { ProgressBar } from "primereact/progressbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { useGetSupportArticleByIdQuery } from "../../store/services/support";
import { renderMarkdownPreview } from "../Forms/SupportArticleForm";
import { SupportArticleListItem } from "../../models/support";
import { formatSupportDateTime } from "../../utils/functions/parseInstant";

interface SupportArticleDetailDialogProps {
  /** Fila de la tabla: pinta el modal al instante mientras vuela el detalle. */
  article?: SupportArticleListItem;
  /** Alternativa a `article` para pantallas que sólo tienen el id (ej: feedback). */
  articleId?: string;
  fallbackCategoryName?: string | null;
  onHide: () => void;
}

const formatDateTime = (value: string): string => formatSupportDateTime(value);

export function SupportArticleDetailDialog({
  article,
  articleId,
  fallbackCategoryName,
  onHide,
}: SupportArticleDetailDialogProps) {
  const openedId = article?.id ?? articleId;

  // El detalle sólo agrega el historial de versiones: el resto ya viene en la fila,
  // así el modal abre con datos y se completa cuando responde el endpoint.
  const { data: articleDetail, isFetching: isFetchingDetail } = useGetSupportArticleByIdQuery(
    openedId ?? "",
    { skip: !openedId }
  );

  const viewedArticle =
    articleDetail && articleDetail.id === openedId
      ? {
          ...articleDetail,
          categoryName:
            articleDetail.categoryName ?? article?.categoryName ?? fallbackCategoryName ?? null,
        }
      : article;

  const viewedBodyHtml = useMemo(() => {
    const body = viewedArticle?.body ?? "";
    if (!body.trim()) return "";
    return body.trimStart().startsWith("<") ? body : renderMarkdownPreview(body);
  }, [viewedArticle?.body]);

  return (
    <Dialog
      visible={!!openedId}
      header={
        <div className="flex align-items-center gap-2">
          <i className="pi pi-file-edit text-primary"></i>
          <span>Detalle del artículo</span>
        </div>
      }
      modal
      style={{ width: "52rem", maxWidth: "95vw", maxHeight: "90vh" }}
      breakpoints={{ "960px": "95vw" }}
      contentStyle={{ overflowY: "auto" }}
      onHide={onHide}
    >
      {!viewedArticle && (
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <ProgressSpinner />
        </div>
      )}

      {viewedArticle && (
        <div className="flex flex-column gap-4">
          <div className="flex flex-column gap-2">
            <h2
              className="text-xl md:text-2xl font-semibold text-900 m-0"
              style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              {viewedArticle.title}
            </h2>
            <div className="flex flex-wrap align-items-center gap-2">
              <Tag
                icon="pi pi-folder"
                severity="secondary"
                value={viewedArticle.categoryName || "Sin categoría"}
                style={{ overflowWrap: "anywhere", maxWidth: "100%" }}
              />
              {viewedArticle.isActive ? (
                <Tag severity="success" value="Activo" />
              ) : (
                <Tag severity="danger" value="Inactivo" />
              )}
              {viewedArticle.isFeatured && <Tag severity="success" value="FAQ" />}
              <Tag severity="secondary" value={`v${viewedArticle.version}`} />
              {isFetchingDetail && <i className="pi pi-spin pi-spinner text-600"></i>}
            </div>
          </div>

          <div className="grid m-0">
            <div className="col-12 md:col-4 p-1">
              <div className="surface-card border-1 surface-border border-round p-3 h-full">
                <small className="text-600 block mb-1">Utilidad</small>
                <span className="text-2xl font-semibold text-900">
                  {viewedArticle.feedbackPct === null ? "—" : `${viewedArticle.feedbackPct}%`}
                </span>
                {viewedArticle.feedbackPct !== null && (
                  <ProgressBar
                    value={viewedArticle.feedbackPct}
                    showValue={false}
                    className="mt-2"
                    style={{ height: "6px" }}
                  />
                )}
              </div>
            </div>
            <div className="col-6 md:col-4 p-1">
              <div className="surface-card border-1 surface-border border-round p-3 h-full">
                <small className="text-600 block mb-1">Les sirvió</small>
                <span className="text-2xl font-semibold text-green-600">
                  <i className="pi pi-thumbs-up text-base mr-2"></i>
                  {viewedArticle.feedbackYes}
                </span>
              </div>
            </div>
            <div className="col-6 md:col-4 p-1">
              <div className="surface-card border-1 surface-border border-round p-3 h-full">
                <small className="text-600 block mb-1">No les sirvió</small>
                <span className="text-2xl font-semibold text-red-500">
                  <i className="pi pi-thumbs-down text-base mr-2"></i>
                  {viewedArticle.feedbackNo}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <div>
              <small className="text-600 block">Creado</small>
              <span className="font-medium">{formatDateTime(viewedArticle.createdAt)}</span>
            </div>
            <div>
              <small className="text-600 block">Última actualización</small>
              <span className="font-medium">{formatDateTime(viewedArticle.updatedAt)}</span>
            </div>
            <div>
              <small className="text-600 block">Orden</small>
              <span className="font-medium">{viewedArticle.sortOrder}</span>
            </div>
          </div>

          <div>
            <small className="text-600 block mb-2">Tags de búsqueda</small>
            {viewedArticle.searchTags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {viewedArticle.searchTags.map((tag) => (
                  <Tag
                    key={tag}
                    value={tag}
                    severity="info"
                    style={{ overflowWrap: "anywhere", maxWidth: "100%" }}
                  />
                ))}
              </div>
            ) : (
              <span className="text-500">Sin tags</span>
            )}
          </div>

          <div>
            <small className="text-600 block mb-2">Contenido</small>
            {viewedBodyHtml ? (
              <div
                className="p-3 border-1 surface-border border-round surface-card"
                style={{
                  maxHeight: "22rem",
                  overflow: "auto",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
                dangerouslySetInnerHTML={{ __html: viewedBodyHtml }}
              />
            ) : (
              <span className="text-500">Sin contenido</span>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
